import React, { useState, FC, useMemo, useRef } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import DataTable from '../../../../reusables/DataTable';
import { Loading } from '@appkit4/react-components/loading';
import { Search } from '@appkit4/react-components/search';
import { Tooltip } from '@appkit4/react-components/tooltip';
import { Select } from '@appkit4/react-components/select';
import Pagination from '../../../../reusables/Pagination';
import useGenericErrorToast from '../../../../../helpers/useGenericErrorToast';
import usePaginationControl from '../../../../../helpers/usePaginationControl';
import useLocale from '../../../../../locale/useLocale';
import { AllUserResponse, useGetAllUsers } from '../../../../../api/users';

export const enum UserStatuses {
    Active = 1,
    Deleted,
}

interface UsersTableProps {
    selectedTenantId?: number;
    title?: string;
}

const UsersTable: FC<UsersTableProps> = () => {
    const { l } = useLocale();

    const userStatuses = [
        { label: 'All Users', value: 'all' },
        { label: l('_active'), value: UserStatuses.Active },
        { label: l('_deleted'), value: UserStatuses.Deleted },
    ];
    const [searchString, setSearchString] = useState<string>('');
    const [userStatus, setUserStatus] = useState<UserStatuses | undefined>();
    const [pageSize, setPageSize] = useState<number>(25);
    const [pageNum, setPageNum] = useState<number>(1);
    const skip = (pageNum - 1) * pageSize;
    const take = pageSize;
    const showGenericErrorToast = useGenericErrorToast();

    /* Load all users regardless of the selected tenant. */
    const { data: paginatedUserResponse, isLoading: loadingUsers } =
        useGetAllUsers(
            {
                skip,
                take,
                searchString,
                userStatus,
            },
            {
                onError: showGenericErrorToast,
            }
        );

    /* prevent current page number being invalid */
    usePaginationControl(
        { totalCount: paginatedUserResponse?.totalUserCount },
        pageSize,
        setPageNum,
        pageNum
    );

    const typingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const doneTypingIntervalms = 1000; //time in ms

    const renderTooltip = (
        row: AllUserResponse,
        field: keyof AllUserResponse
    ) => {
        const value = `${row[field]}`;
        return (
            <Tooltip
                trigger="hover"
                position="top"
                distance={5}
                id={`tooltip-${field}`}
                content={value}
                style={{
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    whiteSpace: 'normal',
                }}
            >
                <span>{value}</span>
            </Tooltip>
        );
    };

    const userColumns: ColumnDef<AllUserResponse, unknown>[] = useMemo(
        () => [
            {
                accessorKey: 'name',
                header: 'Name',
                cell: ({ row }) => renderTooltip(row.original, 'name'),
            },
            {
                accessorKey: 'email',
                header: l('_email'),
                cell: ({ row }) => renderTooltip(row.original, 'email'),
            },
            {
                accessorKey: 'role',
                header: l('_role'),
                cell: ({ row }) => renderTooltip(row.original, 'role'),
            },
            {
                accessorKey: 'status',
                header: 'Status',
                cell: ({ row }) => renderTooltip(row.original, 'status'),
            },
        ],
        []
    );

    return (
        <div className="users-table-section">
            <div className="row">
                {((!loadingUsers && paginatedUserResponse) ||
                    searchString != '') && (
                    <div className="users-table-controls">
                        <div className="users-table-search-field">
                            <Search
                                id="user-search"
                                searchType="primary"
                                placeholder={l('_search')}
                                onSearch={(searchString) =>
                                    setSearchString(searchString)
                                }
                                onChange={(query: string, event) => {
                                    if (typingTimer.current)
                                        clearTimeout(typingTimer.current);
                                    if (!query) {
                                        setSearchString('');
                                    } else {
                                        typingTimer.current = setTimeout(() => {
                                            // simulate pressing enter on the search box
                                            // so the onSearch method handles all the actual searching
                                            event.target.dispatchEvent(
                                                new KeyboardEvent('keydown', {
                                                    code: 'Enter',
                                                    key: 'Enter',
                                                    charCode: 13,
                                                    keyCode: 13,
                                                    view: window,
                                                    bubbles: true,
                                                })
                                            );
                                        }, doneTypingIntervalms);
                                    }
                                }}
                                onClear={() => setSearchString('')}
                                className="searchEngagement"
                            />
                        </div>
                        <div className="users-table-status-field">
                            <Select
                                data={userStatuses}
                                searchable={false}
                                defaultValue="all"
                                placeholder={'User status'}
                                onSelect={(selected) => {
                                    setPageNum(1);
                                    setUserStatus(
                                        selected === 'all'
                                            ? undefined
                                            : (Number(selected) as UserStatuses)
                                    );
                                }}
                                data-testid="user-status-select"
                            />
                        </div>
                    </div>
                )}
            </div>

            {loadingUsers ? (
                <Loading
                    loadingType="circular"
                    indeterminate
                    compact={false}
                    className="mt-2"
                />
            ) : paginatedUserResponse ? (
                <>
                    <DataTable
                        data={paginatedUserResponse.users}
                        columns={userColumns}
                        sorting
                        columnFiltering
                        globalFilter={searchString}
                        rowSelection
                        getRowId={(row) => String(row.id)}
                        resetSelectionKey={pageNum}
                        manualPagination
                        totalRows={paginatedUserResponse.totalUserCount}
                        condensed
                        striped
                    />

                    {paginatedUserResponse.totalUserCount > 0 && (
                        <div data-testid="pagination">
                            <Pagination
                                totalItems={
                                    paginatedUserResponse.totalUserCount
                                }
                                currentPage={pageNum}
                                pageSize={pageSize}
                                onPageChange={setPageNum}
                                onPageSizeChange={(size) => {
                                    setPageSize(size);
                                    setPageNum(1);
                                }}
                            />
                        </div>
                    )}
                </>
            ) : null}
        </div>
    );
};

export default UsersTable;
